import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { formatCurrency } from '../../utils/Currency';

const KPICard = ({
  label,
  value,
  change,
  changeLabel,
  color,
  loading,
  showValues,
  isPercentage,
  invertChangeColor,
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    if (loading || change === null || change === undefined) return null;

    const isPositive = invertChangeColor ? change < 0 : change > 0;
    const isNegative = invertChangeColor ? change > 0 : change < 0;

    if (isPositive) {
      return <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />;
    }
    if (isNegative) {
      return <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />;
    }
    return <TrendingFlatIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />;
  };

  const getChangeColor = () => {
    if (change === null || change === undefined) return theme.palette.text.secondary;
    const isPositive = invertChangeColor ? change < 0 : change > 0;
    const isNegative = invertChangeColor ? change > 0 : change < 0;

    if (isPositive) return theme.palette.success.main;
    if (isNegative) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const formatChange = () => {
    if (change === null || change === undefined) return '';
    const sign = change > 0 ? '+' : '';
    if (isPercentage) {
      return `${sign}${change.toFixed(1)}pp`;
    }
    return `${sign}${change.toFixed(1)}%`;
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderLeft: `4px solid ${color}`,
        boxShadow: theme.shadows[2],
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: theme.palette.text.secondary,
          mb: 1,
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {loading ? (
          <Skeleton width={120} height={32} />
        ) : showValues ? (
          value
        ) : (
          '--'
        )}
      </Typography>
      {changeLabel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {showValues && getTrendIcon()}
          <Typography
            variant="caption"
            sx={{
              color: showValues ? getChangeColor() : theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            {loading ? (
              <Skeleton width={80} />
            ) : showValues ? (
              `${formatChange()} ${changeLabel}`
            ) : (
              ''
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const calculateNetBalanceChange = (data) => {
  if (!data?.netBalanceChange) return null;
  const previousBalance = data.netBalance - data.netBalanceChange;
  if (previousBalance === 0) return data.netBalance > 0 ? 100 : 0;
  return (data.netBalanceChange / Math.abs(previousBalance)) * 100;
};

const BalanceKPICards = ({ data, currency, loading, showValues }) => {
  const theme = useTheme();

  if (!data && !loading) return null;

  const kpis = [
    {
      id: 'cash-in',
      label: 'Receita Total',
      value: formatCurrency(currency, data?.cashIn || 0),
      change: data?.cashInChange,
      changeLabel: 'vs anterior',
      color: theme.palette.success.main,
      invertChangeColor: false,
    },
    {
      id: 'cash-out',
      label: 'Despesa Total',
      value: formatCurrency(currency, data?.cashOut || 0),
      change: data?.cashOutChange,
      changeLabel: 'vs anterior',
      color: theme.palette.error.main,
      invertChangeColor: true,
    },
    {
      id: 'net-balance',
      label: 'Saldo Liquido',
      value: formatCurrency(currency, data?.netBalance || 0),
      change: calculateNetBalanceChange(data),
      changeLabel: 'vs anterior',
      color: theme.palette.info.main,
      invertChangeColor: false,
    },
    {
      id: 'savings-rate',
      label: 'Taxa de Poupanca',
      value: `${(data?.savingsRate || 0).toFixed(1)}%`,
      change: data?.savingsRateChange,
      changeLabel: 'vs anterior',
      color: theme.palette.warning.main,
      invertChangeColor: false,
      isPercentage: true,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
        mb: 3,
      }}
    >
      {kpis.map((kpi) => (
        <KPICard
          key={kpi.id}
          label={kpi.label}
          value={kpi.value}
          change={kpi.change}
          changeLabel={kpi.changeLabel}
          color={kpi.color}
          loading={loading}
          showValues={showValues}
          isPercentage={kpi.isPercentage}
          invertChangeColor={kpi.invertChangeColor}
        />
      ))}
    </Box>
  );
};

export default BalanceKPICards;
